import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type SVGProps,
} from "react";
import {
  Bot,
  Check,
  ChevronRight,
  CircleUserRound,
  FileText,
  IdCard,
  Info,
  LayoutList,
  Link,
  Palette,
  Star,
  Undo2,
} from "lucide-react";
import { normalizeReturnLink } from "../../../lib/tg";
import { driverApi } from "../lib/api";
import { FlowDesignSection } from "./FlowDesignSection";

type FlowStatus =
  | "draft"
  | "on_moderation"
  | "approved"
  | "active"
  | "paused"
  | "finished"
  | "rejected"
  | "disabled";

type Draft = {
  id?: number;
  name: string;
  resource_category: "" | "required_subscription" | "broadcast";
  status?: FlowStatus;
  traffic_sources: string[];
  publication_resources: string[];
  success_url: string;
  final_button_text: string;
  progress_start_text: string;
  progress_middle_text: string;
  progress_final_text: string;
  excluded_resource_ids: number[];
  selected_resource_ids: number[];
  own_resources_order: number[];
  share_url?: string;
};

const EMPTY: Draft = {
  name: "",
  resource_category: "",
  traffic_sources: [""],
  publication_resources: [""],
  success_url: "",
  final_button_text: "забрать доступ",
  progress_start_text: "отличное начало!",
  progress_middle_text: "осталось немного!",
  progress_final_text: "последняя проверка и доступ у тебя!",
  excluded_resource_ids: [],
  selected_resource_ids: [],
  own_resources_order: [],
};

type ResourceOption = {
  id: number;
  name: string;
  public_name?: string | null;
  public_description?: string | null;
  type: string;
  status?: string;
};

type ResourceSelectionRules = {
  available_count: number;
  configured_min_required: number;
  min_required: number;
  max_own_resources_per_driver: number;
};

type Props = {
  mode: "create" | "edit";
  flowId?: number;
  onDone: () => void;
  onCancel: () => void;
};

type SettingsSection = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type SettingsIcon = ComponentType<SVGProps<SVGSVGElement>>;

export function FlowWizard({ mode, flowId, onDone, onCancel }: Props) {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [section, setSection] = useState<SettingsSection | null>(null);
  const [options, setOptions] = useState<ResourceOption[]>([]);
  const [selectionRules, setSelectionRules] =
    useState<ResourceSelectionRules | null>(null);
  const [ownResources, setOwnResources] = useState<ResourceOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessInput, setShowSuccessInput] = useState(false);
  const designFlowId = draft.id ?? flowId;

  useEffect(() => {
    if (mode === "edit" && flowId) {
      driverApi.getFlow(flowId).then((r) => {
        if (r.data) {
          const loaded = { ...EMPTY, ...(r.data as unknown as Partial<Draft>) };
          setDraft({
            ...loaded,
            traffic_sources: ensureList(loaded.traffic_sources),
            publication_resources: ensureList(loaded.publication_resources),
          });
          setShowSuccessInput(Boolean(loaded.success_url));
        }
      });
    }
  }, [mode, flowId]);

  useEffect(() => {
    if (mode !== "edit" || !draft.resource_category) return;
    if (section !== 6 && section !== 7) return;
    setSelectionRules(null);
    driverApi.getResourceSelectionRules(draft.resource_category).then((r) => {
      setSelectionRules(r.data ? (r.data as ResourceSelectionRules) : null);
    });
    if (section === 6) {
      driverApi.listResourceOptions(draft.resource_category).then((r) => {
        setOptions(Array.isArray(r.data) ? (r.data as ResourceOption[]) : []);
      });
    }
    driverApi.listOwnResources({ selectableOnly: true }).then((r) => {
      setOwnResources(
        Array.isArray(r.data) ? (r.data as ResourceOption[]) : [],
      );
    });
  }, [mode, section, draft.resource_category]);

  function patch(p: Partial<Draft>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  async function createFlow() {
    const validationError = validateCreate(draft);
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await driverApi.createFlow({
        name: draft.name.trim(),
        resource_category: draft.resource_category,
        traffic_sources: cleanList(draft.traffic_sources),
        publication_resources: cleanList(draft.publication_resources),
        success_url: "",
        final_button_text: draft.final_button_text,
        progress_start_text: draft.progress_start_text,
        progress_middle_text: draft.progress_middle_text,
        progress_final_text: draft.progress_final_text,
        excluded_resource_ids: [],
        selected_resource_ids: [],
        own_resources_order: [],
      } as never);
      if (r.error || !r.data) {
        setError(
          formatError(r.error, "не удалось отправить поток на модерацию"),
        );
        return;
      }
      onDone();
    } finally {
      setBusy(false);
    }
  }

  async function savePatch(p: Partial<Draft>) {
    if (!draft.id) return;
    setBusy(true);
    setError(null);
    try {
      const payload = { ...p };
      if (payload.traffic_sources)
        payload.traffic_sources = cleanList(payload.traffic_sources);
      if (payload.publication_resources) {
        payload.publication_resources = cleanList(
          payload.publication_resources,
        );
      }
      const r = await driverApi.patchFlow(draft.id, payload as never);
      if (r.error || !r.data) {
        setError(formatError(r.error, "не удалось сохранить настройки"));
        return;
      }
      const saved = r.data as unknown as Partial<Draft>;
      setDraft((d) => ({
        ...d,
        ...saved,
        traffic_sources: ensureList(saved.traffic_sources ?? d.traffic_sources),
        publication_resources: ensureList(
          saved.publication_resources ?? d.publication_resources,
        ),
      }));
      setSection(null);
    } finally {
      setBusy(false);
    }
  }

  function saveSuccessUrl() {
    const successUrl = normalizeReturnLink(draft.success_url);
    if (!successUrl) {
      setError("укажите корректную ссылку http(s) или t.me/…");
      return;
    }
    void savePatch({ success_url: successUrl });
  }

  if (mode === "create") {
    return (
      <div className="tp-wizard">
        <div className="tp-wizard-head tp-flow-header">
          <button
            onClick={onCancel}
            className="tp-flow-back-button"
            aria-label="назад"
          >
            ←
          </button>
          <h1 className="tp-flow-page-title">создание ссылок</h1>
          <span aria-hidden="true" />
        </div>
        {error ? <div className="tp-alert">{error}</div> : null}

        {step === 1 && (
          <div className="space-y-3">
            <h2 className="tp-flow-panel-title">
              <Link className="tp-flow-panel-title-icon" aria-hidden="true" />
              <span>выберите тип ссылок</span>
            </h2>
            <div className="grid gap-2">
              {[
                {
                  value: "required_subscription" as const,
                  label: "обязательная подписка",
                  icon: Star,
                },
                {
                  value: "broadcast" as const,
                  label: "рассылка",
                  icon: LocationStarIcon,
                },
              ].map(({ value, label, icon: ChoiceIcon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={draft.resource_category === value}
                  className="tp-choice-button"
                  data-active={draft.resource_category === value}
                  onClick={() =>
                    patch({
                      resource_category: value as Draft["resource_category"],
                      selected_resource_ids: [],
                      own_resources_order: [],
                    })
                  }
                >
                  <span className="tp-choice-button-main">
                    <ChoiceIcon
                      className="tp-choice-button-icon"
                      aria-hidden="true"
                    />
                    <span>{label}</span>
                  </span>
                  <Check
                    className="tp-choice-button-check"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
            <button
              className="tp-button-primary w-full disabled:cursor-not-allowed disabled:opacity-45"
              disabled={!draft.resource_category}
              onClick={() => setStep(2)}
            >
              далее
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="tp-flow-panel-title tp-flow-panel-title-flush">
              <Link className="tp-flow-panel-title-icon" aria-hidden="true" />
              <span>название для блока ссылок</span>
            </h2>
            <input
              className="tp-input"
              placeholder="например: starsbot — звезды"
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
            <EditableList
              title="аккаунты для публикации ссылок"
              icon={CircleUserRound}
              placeholder="например: @tiktok"
              values={draft.traffic_sources}
              onChange={(traffic_sources) => patch({ traffic_sources })}
            />
            <EditableList
              title="ресурсы для публикации ссылок"
              icon={Bot}
              placeholder="например: @bot"
              values={draft.publication_resources}
              onChange={(publication_resources) =>
                patch({ publication_resources })
              }
            />
            <button
              className="tp-button-primary w-full disabled:opacity-45"
              disabled={busy}
              onClick={createFlow}
            >
              {busy ? "отправляем..." : "далее"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tp-wizard">
      <div className="tp-wizard-head tp-flow-header">
        <button
          onClick={section ? () => setSection(null) : onCancel}
          className="tp-flow-back-button"
          data-compact={section ? "true" : undefined}
          aria-label={section ? "назад" : "закрыть"}
        >
          ←
        </button>
        <SettingsPageTitle />
        <span aria-hidden="true" />
      </div>
      {error ? <div className="tp-alert">{error}</div> : null}

      {section === null ? (
        <div className="space-y-3">
          <div className="grid gap-2">
            <SettingsButton
              icon={Link}
              label="название блока ссылок"
              onClick={() => setSection(1)}
            />
            <SettingsButton
              icon={IdCard}
              label="источники трафика"
              onClick={() => setSection(2)}
            />
            <SettingsButton
              icon={FileText}
              label="текста показа блока ссылок"
              onClick={() => setSection(3)}
            />
            <SettingsButton
              icon={ScanLetterTIcon}
              label="текст финальной кнопки"
              onClick={() => setSection(4)}
            />
            <SettingsButton
              icon={Undo2}
              label="ссылка возврата"
              onClick={() => setSection(5)}
            />
            <SettingsButton
              icon={LayoutList}
              label="витрина ресурсов спонсоров"
              onClick={() => setSection(6)}
            />
            <SettingsButton
              icon={LayersPlusIcon}
              label="добавление своих ресурсов"
              onClick={() => setSection(7)}
            />
            <SettingsButton
              icon={Palette}
              label="дизайн витрины"
              onClick={() => setSection(8)}
            />
          </div>
        </div>
      ) : null}

      {section === 1 && (
        <div className="space-y-3">
          <SettingsTitle icon={Link}>название блока ссылок</SettingsTitle>
          <input
            className="tp-input"
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
          <button
            className="tp-button-primary w-full"
            disabled={busy || !draft.name.trim()}
            onClick={() => savePatch({ name: draft.name.trim() })}
          >
            сохранить
          </button>
        </div>
      )}

      {section === 2 && (
        <div className="space-y-4">
          <EditableList
            title="аккаунты для публикации ссылок"
            icon={CircleUserRound}
            placeholder="например: @tiktok"
            values={draft.traffic_sources}
            onChange={(traffic_sources) => patch({ traffic_sources })}
          />
          <EditableList
            title="ресурсы для публикации ссылок"
            icon={Bot}
            placeholder="например: @bot"
            values={draft.publication_resources}
            onChange={(publication_resources) =>
              patch({ publication_resources })
            }
          />
          <button
            className="tp-button-primary w-full"
            disabled={
              busy ||
              cleanList(draft.traffic_sources).length === 0 ||
              cleanList(draft.publication_resources).length === 0
            }
            onClick={() =>
              savePatch({
                traffic_sources: draft.traffic_sources,
                publication_resources: draft.publication_resources,
              })
            }
          >
            сохранить
          </button>
        </div>
      )}

      {section === 3 && (
        <div className="space-y-3">
          <SettingsTitle icon={FileText}>
            текста показа блока ссылок
          </SettingsTitle>
          <label className="grid gap-1.5 text-sm">
            <span className="text-muted-foreground">от 10%</span>
            <input
              className="tp-input"
              value={draft.progress_start_text}
              onChange={(e) => patch({ progress_start_text: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5 text-sm">
            <span className="text-muted-foreground">от 50%</span>
            <input
              className="tp-input"
              value={draft.progress_middle_text}
              onChange={(e) => patch({ progress_middle_text: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5 text-sm">
            <span className="text-muted-foreground">от 90%</span>
            <input
              className="tp-input"
              value={draft.progress_final_text}
              onChange={(e) => patch({ progress_final_text: e.target.value })}
            />
          </label>
          <button
            className="tp-button-primary w-full"
            disabled={busy}
            onClick={() =>
              savePatch({
                progress_start_text: draft.progress_start_text,
                progress_middle_text: draft.progress_middle_text,
                progress_final_text: draft.progress_final_text,
              })
            }
          >
            сохранить
          </button>
          <SettingsHint>
            укажите подсказки, которые будут меняться на витрине по мере
            выполнения ресурсов
          </SettingsHint>
        </div>
      )}

      {section === 4 && (
        <div className="space-y-3">
          <SettingsTitle icon={ScanLetterTIcon}>
            текст финальной кнопки
          </SettingsTitle>
          <input
            className="tp-input"
            placeholder="забрать доступ"
            value={draft.final_button_text}
            onChange={(e) => patch({ final_button_text: e.target.value })}
          />
          <button
            className="tp-button-primary w-full"
            disabled={busy || !draft.final_button_text.trim()}
            onClick={() =>
              savePatch({ final_button_text: draft.final_button_text.trim() })
            }
          >
            сохранить
          </button>
          {draft.final_button_text.trim() &&
          !normalizeReturnLink(draft.success_url) ? (
            <div className="tp-alert">
              без ссылки возврата кнопка на витрине не появится: добавьте её в
              разделе «ссылка возврата»
            </div>
          ) : null}
          <SettingsHint>
            кнопка будет в закрытом виде до полной подписки на всех спонсоров
          </SettingsHint>
        </div>
      )}

      {section === 5 && (
        <div className="space-y-3">
          <SettingsTitle icon={Undo2}>ссылка возврата</SettingsTitle>
          {!showSuccessInput && !draft.success_url ? (
            <button
              className="tp-button-secondary w-full"
              onClick={() => setShowSuccessInput(true)}
            >
              + добавить
            </button>
          ) : (
            <>
              <input
                className="tp-input"
                placeholder="https://t.me/MyBot?start=movie1"
                value={draft.success_url}
                onChange={(e) => patch({ success_url: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  className="tp-button-secondary"
                  onClick={() => {
                    patch({ success_url: "" });
                    setShowSuccessInput(false);
                  }}
                >
                  убрать
                </button>
                <button
                  className="tp-button-primary"
                  disabled={busy || !draft.success_url.trim()}
                  onClick={saveSuccessUrl}
                >
                  сохранить
                </button>
              </div>
            </>
          )}
          <SettingsHint>
            ссылка откроется внутри Telegram после успешной проверки всех
            ресурсов; укажите полный адрес https://…
          </SettingsHint>
        </div>
      )}

      {section === 6 && (
        <div className="space-y-3">
          <SponsorResourcesSection
            draft={draft}
            options={options}
            rules={selectionRules}
            busy={busy}
            onPatch={patch}
            onSave={(selectedIds, allIds) =>
              savePatch({
                selected_resource_ids:
                  selectedIds.length === allIds.length ? [] : selectedIds,
              })
            }
          />
        </div>
      )}

      {section === 7 && (
        <div className="space-y-3">
          <OwnResourcesSection
            draft={draft}
            resources={ownResources}
            maxOwnResources={
              selectionRules?.max_own_resources_per_driver ??
              ownResources.length
            }
            busy={busy}
            onPatch={patch}
            onSave={() =>
              savePatch({ own_resources_order: draft.own_resources_order })
            }
          />
        </div>
      )}

      {section === 8 && designFlowId ? (
        <FlowDesignSection flowId={designFlowId} flowName={draft.name} />
      ) : null}
    </div>
  );
}

function SettingsPageTitle() {
  return <h1 className="tp-flow-page-title">панель настроек</h1>;
}

function EditableList({
  title,
  icon: Icon,
  placeholder,
  values,
  onChange,
}: {
  title: string;
  icon?: SettingsIcon;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const safeValues = values.length > 0 ? values : [""];
  return (
    <div className="space-y-2">
      <p className="tp-editable-title">
        {Icon ? (
          <Icon className="tp-editable-title-icon" aria-hidden="true" />
        ) : null}
        <span>{title}</span>
      </p>
      {safeValues.map((value, index) => (
        <div key={index} className="grid grid-cols-[1fr_auto] gap-2">
          <input
            className="tp-input"
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              const next = [...safeValues];
              next[index] = e.target.value;
              onChange(next);
            }}
          />
          {safeValues.length > 1 ? (
            <button
              className="tp-button-ghost min-w-11"
              type="button"
              aria-label="скрыть поле"
              onClick={() => onChange(safeValues.filter((_, i) => i !== index))}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        className="tp-button-secondary w-full"
        onClick={() => onChange([...safeValues, ""])}
      >
        + добавить
      </button>
    </div>
  );
}

function SettingsTitle({
  icon: Icon,
  children,
}: {
  icon: SettingsIcon;
  children: string;
}) {
  return (
    <h2 className="tp-settings-title">
      <Icon className="tp-settings-title-icon" aria-hidden="true" />
      <span>{children}</span>
    </h2>
  );
}

function SettingsHint({ children }: { children: string }) {
  return (
    <p className="tp-settings-hint">
      <Info className="tp-settings-hint-icon" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

function SettingsButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: SettingsIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="tp-button-secondary tp-settings-button justify-start gap-3 text-left"
      onClick={onClick}
    >
      <Icon className="tp-settings-button-icon" aria-hidden="true" />
      <span className="min-w-0 flex-1">{label}</span>
      <ChevronRight className="tp-settings-chevron-icon" aria-hidden="true" />
    </button>
  );
}

function SponsorResourcesSection({
  draft,
  options,
  rules,
  busy,
  onPatch,
  onSave,
}: {
  draft: Draft;
  options: ResourceOption[];
  rules: ResourceSelectionRules | null;
  busy: boolean;
  onPatch: (patch: Partial<Draft>) => void;
  onSave: (selectedIds: number[], allIds: number[]) => void;
}) {
  const allIds = useMemo(() => options.map((item) => item.id), [options]);
  const minRequired = Math.min(
    options.length,
    Math.max(0, rules?.min_required ?? 0),
  );
  const selectedIds = useMemo(() => {
    const allowed = new Set(allIds);
    const explicit = draft.selected_resource_ids.filter((id) =>
      allowed.has(id),
    );
    const ids = explicit.length > 0 ? explicit : allIds;
    if (ids.length < minRequired && options.length <= minRequired)
      return allIds;
    return ids;
  }, [allIds, draft.selected_resource_ids, minRequired, options.length]);
  const selected = new Set(selectedIds);
  const canSave = selectedIds.length >= minRequired;

  function toggle(id: number) {
    if (selected.has(id) && selected.size <= minRequired) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const normalized = allIds.filter((itemId) => next.has(itemId));
    onPatch({
      selected_resource_ids:
        normalized.length === allIds.length ? [] : normalized,
    });
  }

  return (
    <div className="space-y-3">
      <SettingsTitle icon={LayoutList}>
        витрина ресурсов спонсоров
      </SettingsTitle>
      {options.length === 0 ? (
        <div className="tp-card-soft text-sm text-muted-foreground">
          для выбранного типа пока нет активных ресурсов
        </div>
      ) : (
        <div className="space-y-2">
          {options.map((resource) => (
            <button
              key={resource.id}
              type="button"
              aria-pressed={selected.has(resource.id)}
              className="tp-choice-button"
              data-active={selected.has(resource.id)}
              disabled={
                selected.has(resource.id) && selected.size <= minRequired
              }
              onClick={() => toggle(resource.id)}
            >
              <span className="tp-choice-button-main min-w-0 flex-1">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {resource.public_name || resource.name}
                  </span>
                  {resource.public_description ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">
                      {resource.public_description}
                    </span>
                  ) : null}
                </span>
              </span>
              <Check className="tp-choice-button-check" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      <button
        className="tp-button-primary w-full"
        disabled={busy || !canSave}
        onClick={() => onSave(selectedIds, allIds)}
      >
        сохранить
      </button>
      {!canSave ? (
        <p className="text-xs text-red-400">
          выберите минимум {minRequired} ресурсов спонсоров
        </p>
      ) : null}
    </div>
  );
}

function OwnResourcesSection({
  draft,
  resources,
  maxOwnResources,
  busy,
  onPatch,
  onSave,
}: {
  draft: Draft;
  resources: ResourceOption[];
  maxOwnResources: number;
  busy: boolean;
  onPatch: (patch: Partial<Draft>) => void;
  onSave: () => void;
}) {
  const limit = Math.max(0, maxOwnResources);
  const selected = new Set(draft.own_resources_order);
  // Отключённый ресурс исчезает из списка, но остаётся в own_resources_order.
  // Считать лимит по нему нельзя: два таких «призрака» занимали оба слота и
  // блокировали выбор целиком — кнопки не нажимались вообще.
  const selectableIds = new Set(
    resources.filter((r) => isSelectableOwnStatus(r.status)).map((r) => r.id),
  );
  const liveSelectedCount = draft.own_resources_order.filter((id) =>
    selectableIds.has(id),
  ).length;
  function toggle(id: number) {
    const next = selected.has(id)
      ? draft.own_resources_order.filter((item) => item !== id)
      : [
          ...draft.own_resources_order.filter((item) =>
            selectableIds.has(item),
          ),
          id,
        ].slice(0, limit);
    onPatch({ own_resources_order: next });
  }

  return (
    <div className="space-y-3">
      <SettingsTitle icon={LayersPlusIcon}>
        добавление своих ресурсов
      </SettingsTitle>
      <div className="text-sm text-muted-foreground">
        можно выбрать до {limit}
      </div>
      {resources.length === 0 ? (
        <div className="tp-card-soft text-sm text-muted-foreground">
          личных ресурсов нет
        </div>
      ) : (
        <div className="space-y-2">
          {resources.map((resource) => {
            const active = isSelectableOwnStatus(resource.status);
            const selectedResource = selected.has(resource.id);
            const disabled =
              !active || (!selectedResource && liveSelectedCount >= limit);
            return (
              <button
                key={resource.id}
                type="button"
                aria-pressed={selectedResource}
                className="tp-choice-button"
                data-active={selectedResource}
                disabled={disabled}
                onClick={() => toggle(resource.id)}
              >
                <span className="tp-choice-button-main min-w-0 flex-1">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {resource.public_name || resource.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {active
                        ? "можно добавить"
                        : statusLabel(
                            (resource.status ?? "draft") as FlowStatus,
                          )}
                    </span>
                  </span>
                </span>
                <Check className="tp-choice-button-check" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}
      <button
        className="tp-button-primary w-full"
        disabled={busy}
        onClick={onSave}
      >
        сохранить
      </button>
    </div>
  );
}

function ScanLetterTIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M9 8h6" />
      <path d="M12 8v8" />
      <path d="M3 7v-2a2 2 0 0 1 2 -2h2" />
      <path d="M3 17v2a2 2 0 0 0 2 2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M17 21h2a2 2 0 0 0 2 -2v-2" />
    </svg>
  );
}

function LayersPlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 .83.18 2 2 0 0 0 .83-.18l8.58-3.9a1 1 0 0 0 0-1.831z" />
      <path d="M16 17h6" />
      <path d="M19 14v6" />
      <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 .825.178" />
      <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l2.116-.962" />
    </svg>
  );
}

function LocationStarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M10.336 14.672 10 14 3 10.5a.55.55 0 0 1 0-1L21 3l-2.565 7.104" />
      <path d="m17.8 20.817-2.172 1.138a.392.392 0 0 1-.568-.41l.415-2.411-1.757-1.707a.389.389 0 0 1 .217-.665l2.428-.352 1.086-2.193a.392.392 0 0 1 .702 0l1.086 2.193 2.428.352a.39.39 0 0 1 .217.665l-1.757 1.707.414 2.41a.39.39 0 0 1-.567.411L17.8 20.817" />
    </svg>
  );
}

function validateCreate(draft: Draft): string | null {
  if (!draft.resource_category) return "выберите тип ссылок";
  if (!draft.name.trim()) return "укажите название блока ссылок";
  if (cleanList(draft.traffic_sources).length === 0) {
    return "укажите минимум один источник трафика";
  }
  if (cleanList(draft.publication_resources).length === 0) {
    return "укажите минимум один ресурс публикации";
  }
  return null;
}

function ensureList(values: string[] | undefined): string[] {
  const cleaned = cleanList(values ?? []);
  return cleaned.length > 0 ? cleaned : [""];
}

function cleanList(values: string[]): string[] {
  return values.map((item) => item.trim()).filter(Boolean);
}

function isSelectableOwnStatus(status?: string): boolean {
  const value = (status ?? "").toLowerCase();
  return value === "active" || value === "approved";
}

function statusLabel(status: FlowStatus | string): string {
  const labels: Record<string, string> = {
    draft: "черновик",
    on_moderation: "на модерации",
    approved: "одобрен",
    active: "активен",
    paused: "пауза",
    finished: "завершён",
    rejected: "отклонен",
    disabled: "отключён",
  };
  return labels[String(status).toLowerCase()] ?? String(status);
}

function formatError(error: unknown, fallback: string): string {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  const payload = error as {
    error?: { message?: string; code?: string };
    detail?: string | { code?: string; min_required?: number };
  };
  if (typeof payload.detail === "object" && payload.detail?.code) {
    if (payload.detail.code === "selected_resource_min_required") {
      return `выберите минимум ${payload.detail.min_required ?? ""} ресурсов спонсоров`.trim();
    }
    return payload.detail.code;
  }
  const detail =
    typeof payload.detail === "string" ? payload.detail : undefined;
  return payload.error?.message ?? payload.error?.code ?? detail ?? fallback;
}
